/**
 * FormattedJsonView — renders an arbitrary JSON-shaped value as an indented,
 * human-readable definition list. Snake_case keys are humanised via the
 * shared `titleCaseKey` helper (`mortgagees_applied` → `"Mortgagees Applied"`).
 *
 * Used by `WorkflowResultSection`'s JSON fallback path (and re-usable in
 * the Outlook plugin's drawer) in place of a raw `JSON.stringify` dump.
 *
 * Rendering rules:
 *   - primitives (string / number / boolean) → key + value row
 *   - null / undefined                       → key + "—"
 *   - array of primitives                    → joined with commas
 *   - array of objects                       → "Key" header, then each item
 *     rendered as a nested object labelled `Item 1`, `Item 2`, …
 *   - plain object                           → "Key" header, then each
 *     property rendered recursively one indent deeper
 *   - image URLs                             → label has `_uri` / `_url`
 *     suffix dropped, and the value renders as a clickable preview that
 *     opens the shared <ArtifactLightbox> on click — same image surface the
 *     Artifacts section and HITL evidence already use.
 *
 * Non-image URLs still render as plain anchor tags so download/artifact
 * links stay clickable.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { titleCaseKey } from "../schemaHelpers";
import { StoredFileLink } from "~/common/files/StoredFileLink";
import { isStoredFileUrl, isUnavailableStoredUrl, storedFileLabel } from "~/common/files/fileRoute";
import ArtifactLightbox from "./ArtifactLightbox";
import { TruncatedText } from "./TruncatedText";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";

export interface FormattedJsonViewProps {
  data: unknown;
  /** Custom test id for the outer container. */
  testId?: string;
  /** Optional override label for the root node — rarely needed. */
  rootLabel?: string;
  /**
   * Opt-in predicate: when it returns true for a string value, that value is
   * rendered via <TruncatedText> (collapsed preview + "Show more") instead of
   * the default full / <pre> rendering. Applies to primitive strings AND
   * elements of primitive arrays. Default (undefined) ⇒ no truncation, so
   * other callers (ApiEvidenceView, HITL renderers) are unaffected.
   */
  shouldTruncate?: (value: unknown) => boolean;
}

const INDENT_PX = 14;
const URL_RE = /^(?:https?:\/\/\S+|\/api\/files\/\S+)$/i;
// Image-URL detector — matches common image extensions whether or not the
// URL carries a query string (SAS-signed blob URLs always do). Run against
// the URL's path portion only so a query like `?type=png` doesn't trigger
// false positives.
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
// Trailing `_uri` / `_url` on a snake_case key — stripped before titleCaseKey
// so `screenshot_uri` renders as "Screenshot" instead of "Screenshot Uri".
const URI_KEY_SUFFIX_RE = /_(uri|url)$/i;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isPrimitive(v: unknown): boolean {
  return (
    v === null ||
    v === undefined ||
    typeof v === "string" ||
    typeof v === "number" ||
    typeof v === "boolean"
  );
}

function isImageUrl(value: unknown): value is string {
  if (typeof value !== "string" || !URL_RE.test(value)) return false;
  if (isUnavailableStoredUrl(value)) return false;
  // Strip query string before extension check — SAS-signed blob URLs always
  // have one and the file extension lives in the path part.
  const path = value.split("?", 1)[0] ?? value;
  return IMAGE_EXT_RE.test(path);
}

// Inline preview button (icon, no thumbnail — story 25240) that opens the shared ArtifactLightbox on click.
// Matches the visual treatment of HitlEvidencePreview but without the
// cropped-region overlay (no region data here — this is for plain URLs in
// arbitrary JSON shapes).
function ImagePreviewLink({ url, label }: { url: string; label: string }) {
  const [open, setOpen] = useState(false);
  const artifacts: WorkflowArtifact[] = [
    { url, step_name: label, kind: "screenshot" },
  ];
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-2 py-1 max-w-full rounded overflow-hidden border border-primary-gray-light-200 hover:border-sunrise transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sunrise"
        aria-label={`View ${label}`}
        data-testid="formatted-json-image-preview"
      >
        <span aria-hidden="true" className="text-lg">🖼</span>
        <span className="text-xs text-primary-gray-light-700 truncate">{label}</span>
      </button>
      {open && (
        <ArtifactLightbox
          open={open}
          artifacts={artifacts}
          startIndex={0}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Strings ≥ this many chars OR containing a newline get the ``<pre>`` block
// treatment (same monospace / whitespace-pre-wrap / scroll-on-overflow
// styling ``WorkflowResultSection.tsx`` uses for pre-formatted result
// artifacts). Below this threshold, single-line strings keep the inline
// ``<span>`` rendering so a 12-char ``policy_number`` doesn't get a 3-line
// code-block treatment.
const LONG_STRING_THRESHOLD = 200;

function isPreformattedString(s: string): boolean {
  return s.length >= LONG_STRING_THRESHOLD || /[\r\n]/.test(s);
}

function renderPrimitive(value: unknown): ReactNode {
  if (value === null || value === undefined) return <span className="text-primary-gray-light-400">—</span>;
  if (typeof value === "boolean") {
    return (
      <span className={value ? "text-primary-success-700" : "text-primary-gray-light-700"}>
        {String(value)}
      </span>
    );
  }
  if (typeof value === "string" && URL_RE.test(value)) {
    if (isStoredFileUrl(value) || isUnavailableStoredUrl(value)) {
      return (
        <StoredFileLink url={value} className="text-sunrise break-all">
          {storedFileLabel(value)}
        </StoredFileLink>
      );
    }
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sunrise break-all"
      >
        {value}
      </a>
    );
  }
  // Long / multi-line strings render in a Results-style <pre> code block so
  // HTML responses (e.g. ``api-evidence/NNN_graph_get_message_body_*.json``
  // ``response`` field — 8.9 KB of templated HTML) become scannable instead
  // of a single 8.9 KB break-words line. Matches the styling
  // ``WorkflowResultSection`` uses for its pre-formatted artifact path.
  if (typeof value === "string" && isPreformattedString(value)) {
    return (
      <pre
        className="text-2xs font-mono bg-primary-gray-light-50 rounded-md p-2 max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-primary-gray-light-800 border border-primary-gray-light-200"
        data-testid="formatted-json-pre-string"
      >
        {value}
      </pre>
    );
  }
  return <span className="break-words">{String(value)}</span>;
}

interface NodeProps {
  value: unknown;
  label?: string;
  depth: number;
  shouldTruncate?: (value: unknown) => boolean;
}

function JsonNode({ value, label, depth, shouldTruncate }: NodeProps) {
  const indent = depth * INDENT_PX;

  // Image URL — render an inline preview that opens the shared lightbox
  // on click. Bypasses the standard primitive row so the URL string is
  // hidden in favor of a thumbnail.
  if (isImageUrl(value)) {
    return (
      <div
        className="flex gap-3 text-xs py-0.5 items-start"
        style={{ paddingLeft: indent }}
        data-testid="formatted-json-image-row"
      >
        {label && (
          <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0 pt-1">
            {label}
          </span>
        )}
        <span className="flex-1 min-w-0">
          <ImagePreviewLink url={value} label={label || "Image"} />
        </span>
      </div>
    );
  }

  // Primitive — key + value on one row. The value wrapper is a ``<div>``
  // (block-level) rather than a ``<span>`` so block children like the
  // ``<pre>`` block returned by ``renderPrimitive`` for long/multi-line
  // strings render cleanly inside the flex row. For short/inline string
  // primitives the div still flows as if it were an inline span — flexbox
  // doesn't care about the child's display so the visual is identical.
  if (isPrimitive(value)) {
    // Truncatable blob (e.g. base64 attachment bytes) → collapse behind a
    // "Show more" toggle. Checked before the plain/long-string render below.
    const truncate = typeof value === "string" && !!shouldTruncate?.(value);
    const isBlock =
      truncate || (typeof value === "string" && isPreformattedString(value));
    return (
      <div
        className={`flex gap-3 text-xs py-0.5 ${isBlock ? "items-start" : ""}`}
        style={{ paddingLeft: indent }}
      >
        {label && (
          <span className={`text-primary-gray-light-500 min-w-[8rem] flex-shrink-0 ${isBlock ? "pt-1" : ""}`}>
            {label}
          </span>
        )}
        <div className="text-primary-gray-light-900 flex-1 min-w-0">
          {truncate ? (
            <TruncatedText value={value as string} />
          ) : (
            renderPrimitive(value)
          )}
        </div>
      </div>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="flex gap-3 text-xs py-0.5" style={{ paddingLeft: indent }}>
          {label && (
            <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0">
              {label}
            </span>
          )}
          <span className="text-primary-gray-light-400">(empty)</span>
        </div>
      );
    }

    // Array of primitives — show inline, comma-joined. When a truncate
    // predicate is supplied and any element is a blob (e.g. the base64 element
    // of ``draft_attached_sigs = [[name, b64]]``), stack the elements instead
    // and collapse each blob behind a "Show more" toggle; short elements stay
    // plain. Without a truncatable element the comma-joined span is unchanged.
    if (value.every(isPrimitive)) {
      const hasBlob =
        !!shouldTruncate && value.some((v) => shouldTruncate(v));
      if (hasBlob) {
        return (
          <div
            className="flex gap-3 text-xs py-0.5 items-start"
            style={{ paddingLeft: indent }}
          >
            {label && (
              <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0 pt-1">
                {label}
              </span>
            )}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {value.map((v, i) => (
                <div
                  key={i}
                  className="text-primary-gray-light-900 break-words min-w-0"
                >
                  {typeof v === "string" && shouldTruncate(v) ? (
                    <TruncatedText value={v} />
                  ) : v === null || v === undefined ? (
                    "—"
                  ) : (
                    String(v)
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      }
      return (
        <div className="flex gap-3 text-xs py-0.5" style={{ paddingLeft: indent }}>
          {label && (
            <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0">
              {label}
            </span>
          )}
          <span className="text-primary-gray-light-900 break-words flex-1 min-w-0">
            {value
              .map((v) => (v === null || v === undefined ? "—" : String(v)))
              .join(", ")}
          </span>
        </div>
      );
    }

    // Array of objects (or mixed) — labelled header + nested items
    return (
      <>
        {label && (
          <div
            className="text-xs font-semibold text-primary-gray-light-700 pt-1"
            style={{ paddingLeft: indent }}
          >
            {label}
          </div>
        )}
        {value.map((item, idx) => (
          <JsonNode
            key={idx}
            value={item}
            label={`Item ${idx + 1}`}
            depth={label ? depth + 1 : depth}
            shouldTruncate={shouldTruncate}
          />
        ))}
      </>
    );
  }

  // Plain object — labelled header + nested entries
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return (
        <div className="flex gap-3 text-xs py-0.5" style={{ paddingLeft: indent }}>
          {label && (
            <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0">
              {label}
            </span>
          )}
          <span className="text-primary-gray-light-400">(empty)</span>
        </div>
      );
    }
    return (
      <>
        {label && (
          <div
            className="text-xs font-semibold text-primary-gray-light-700 pt-1"
            style={{ paddingLeft: indent }}
          >
            {label}
          </div>
        )}
        {entries.map(([k, v]) => {
          // For image-URL values, drop the `_uri` / `_url` suffix from the
          // key before title-casing so `screenshot_uri` renders as
          // "Screenshot" instead of "Screenshot Uri".
          const displayKey = isImageUrl(v)
            ? k.replace(URI_KEY_SUFFIX_RE, "")
            : k;
          return (
            <JsonNode
              key={k}
              value={v}
              label={titleCaseKey(displayKey)}
              depth={label ? depth + 1 : depth}
              shouldTruncate={shouldTruncate}
            />
          );
        })}
      </>
    );
  }

  // Fallback — String() it
  return (
    <div className="flex gap-3 text-xs py-0.5" style={{ paddingLeft: indent }}>
      {label && (
        <span className="text-primary-gray-light-500 min-w-[8rem] flex-shrink-0">
          {label}
        </span>
      )}
      <span className="text-primary-gray-light-900 break-words flex-1 min-w-0">
        {String(value)}
      </span>
    </div>
  );
}

export function FormattedJsonView({
  data,
  testId = "formatted-json-view",
  rootLabel,
  shouldTruncate,
}: FormattedJsonViewProps) {
  return (
    <div
      className="rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2 flex flex-col gap-0.5"
      data-testid={testId}
    >
      <JsonNode
        value={data}
        label={rootLabel}
        depth={0}
        shouldTruncate={shouldTruncate}
      />
    </div>
  );
}

export default FormattedJsonView;
